<template>
  <button
    :disabled="disabled"
    :class="[
      {
        'is-outlined': !!outlined,
        'is-fullwidth': !!fullwidth,
        'is-borderless': !!borderless,
        'has-shadow' : !!shadow,
        'is-quiet' : !!quiet,
        'is-light' : !!light
      },
      `single-button-${appearance}`,
    ]"
    :type="submit ? 'submit' : null"
    :style="buttonStyle"
    class="button single-button"
    @click="handleClick"
  >
    <i
      v-if="icon && iconPosition === 'left'"
      :class="[`icon-${icon}`]"
      class="single-button-icon"
    />
    <span
      v-if="$slots.default"
      :class="[
        {'is-uppercase': uppercase},
        `has-text-weight-${weight}`,
        {'has-padding-left-1': icon && iconPosition === 'left'},
        {'has-padding-right-1': icon && iconPosition === 'right'}
      ]"
      class="is-flex align-center"
    >
      <slot />
    </span>
    <i
      v-if="icon && iconPosition === 'right'"
      :class="[`icon-${icon}`]"
      class="single-button-icon"
    />
    <input
      v-if="upload"
      v-show="false"
      ref="uploadInput"
      :accept="accept"
      :multiple="multipleUpload"
      type="file"
      name="uploadInput"
      @input="handleUpload"
      @click="$refs.uploadInput.value = null"
    >
  </button>
</template>

<script>

export default {
  name: 'SingleButton',
  props: {
    appearance: {
      type: String,
      default: 'primary',
      validator: val => ['primary', 'secondary', 'tertiary', 'rounded-inactive', 'rounded', 'upload', 'agent', 'danger'].includes(val),
    },
    icon: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    fullwidth: { type: Boolean, default: false },
    outlined: { type: Boolean, default: false },
    uppercase: { type: Boolean, default: false },
    borderless: { type: Boolean, default: false },
    shadow: { type: Boolean, default: false },
    quiet: { type: Boolean, default: false },
    light: { type: Boolean, default: false },
    submit: { type: Boolean, default: false },
    upload: { type: Boolean, default: false },
    accept: { type: String, default: '' },
    multipleUpload: { type: Boolean, default: false },
    weight: {
      type: String,
      default: 'medium',
      validator: val => ['normal', 'medium'].includes(val),
    },
    height: { type: Number, default: 40 },
    iconPosition: { type: String, default: 'left', validator: val => ['left', 'right'].includes(val) },
  },
  emits: ['click', 'upload'],
  computed: {
    buttonStyle() {
      return Object.assign({
        height: `${this.height}px`,
      });
    },
  },
  methods: {
    handleClick(evt) {
      if (this.upload) {
        this.$refs.uploadInput.click();
      } else {
        this.$emit('click', evt);
      }
    },
    handleUpload(evt) {
      if (evt.target.files.length) this.$emit('upload', Array.from(evt.target.files));
    },
  },
};
</script>

<style lang="scss">

.single-button {
  transition: $transition-ease-in-out;
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 1.5rem;
  font-size: $size-9;

  .single-button-icon {
    font-size: $size-6;
  }

  &:focus,
  &:active:focus {
    outline: none;
  }

  &.has-shadow {
    box-shadow: 0 2px 10px 0 rgba(23,132,251,0.30);
  }

  &.single-button-primary.is-outlined.is-quiet {
    color: $blue-sky;
    background-color: #F0F8FF;
    border: none;
  }

  &.single-button-primary{
    background-color: $interactions-primary;
    border-color: $interactions-primary;
    color: $white;
    border-radius: 5px;
  }

  &.single-button-primary[disabled]{
    background-color: $interactions-primary;
    color: $white;
  }

  &.single-button-primary:hover:enabled{
    background-color: $white;
    border-color: $interactions-primary;
    color: $interactions-primary;
  }

  &.single-button-primary.is-outlined {
    background-color: #f1f9ff;
    color: $interactions-primary;
  }

  &.single-button-primary.is-outlined.is-borderless:hover{
    background-color: $white;
    color: $interactions-primary;
  }

  // &.is-primary:hover{
  //   background-color: $primary-invert;
  //   border-color: $primary;
  //   color: $primary;
  // }

  // &.is-primary.is-outlined.is-borderless:hover{
  //   background-color: $primary-invert;
  //   color: $primary;
  // }

  &.single-button-secondary:hover{
    background-color: $secondary-invert;
    border-color: $secondary;
    color: $secondary;
  }

  &.single-button-secondary.is-outlined.is-borderless:hover{
    background-color: $secondary-invert;
    color: $secondary;
  }

  &.single-button-tertiary{
    background-color: $red-radical;
    border-color: $white;
    color: $white;
  }

  &.single-button-tertiary:hover{
    background-color: $white;
    border-color: $red-radical;
    color: $red-radical;
  }

  &.single-button-tertiary.is-outlined.is-borderless:hover{
    background-color: $white;
    color: $red-radical;
  }

  &.single-button-rounded-inactive {
    font-weight: $weight-medium;
    line-height: $size-8;
    background-color: #ECECEC;
    color: #7F7F7F;
    border: none;
    border-radius: 50px;
    height: 34px;
  }
  &.single-button-rounded-inactive:hover {
    background-color: $blue-sky;
    box-shadow: 0 0 15px 0 rgba(0,122,255,0.15);
    color: $white;
    border: none;
  }

  &.single-button-rounded {
    background-color: $blue-sky;
    color: $white;
    border-radius: 50px;
    font-weight: $weight-medium;
    line-height: $size-8;
    box-shadow: 0 0 15px 0 rgba(0,122,255,0.15);
    border: none;
    height: 34px;
  }
  &.single-button-rounded:hover:enabled{
    background-color: $white;
    border-color: $interactions-primary;
    color: $interactions-primary;
    border: none;
    box-shadow: 0 0 15px 0 rgba(0,122,255,0.15);
  }

  &.single-button-upload {
    background-color: $white-old;
    border-color: $grey-snow;
    color: #6B7A8A;
    border-radius: 5px;
  }
  &.single-button-upload:enabled {
    box-shadow: none;
  }

  &.single-button-agent{
    background-color: $white;
    border: none;
    color: $red-radical;
    border-radius: 5px;
    font-weight: $weight-medium;
    padding: 6px 12px;
    box-shadow: 0 0 2px 0 rgba(0,0,0,0.30);
  }
  &.single-button-agent:hover:enabled{
    color: rgba(255, 45, 85, 0.5);
  }

  &.single-button-danger{
    background-color: $lavender-blush;
    border-color: $red-orange;
    color: $red-orange;
    border-radius: 5px;
  }

  &.single-button-danger:hover:enabled{
    background-color: $white;
    color: $red-orange;
  }

  &.single-button-danger.is-outlined.is-quiet {
    color: $red-orange;
    background-color: $lavender-blush;
    border: none;
  }

  &.single-button-danger.is-outlined {
    background-color: $white;
    color: $red-orange;
  }

  &.single-button-danger.is-outlined.is-borderless:hover{
    background-color: $lavender-blush;
    color: $red-orange;
  }
}


</style>
